import dynamic from 'next/dynamic';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdArrowForward, MdOutlineArrowBack } from 'react-icons/md';
import BtnConfirm from 'ui/components/atoms/BtnConfirm';
import StructuredData from 'ui/components/atoms/StructuredData';
import InfoDigiroom from '../../molecules/InfoDigiroom';
import BreadCrumbs, { IBreadcrumbs } from 'ui/components/molecules/Breadcrumbs';
import DetailArticleHeroSection from 'ui/components/organism/DetailArticleHeroSection';
import DetailArticleAnotherPromoSection from 'ui/components/organism/DetailArticleAnotherPromoSection';
import DetailArticleAnotherArticlesSection from 'ui/components/organism/DetailArticleAnotherArticlesSection';
import SocialMediaLinksSection from 'ui/components/organism/SocialMediaLinksSection';
import DetailArticleBodySection from 'ui/components/organism/DetailArticleBodySection';
import { setMetadata } from 'ui/store/metadata/actions';
import eventClickInterest from 'ui/lib/gtm/eventClickInterest';
import pageViewNewsAnother from 'ui/lib/gtm/pageViewNewsAnother';
import pageViewPromoAnother from 'ui/lib/gtm/pageViewPromoAnother';
import { sendDataInChunks } from 'ui/lib/collectData';
import useElementVisibility from '../../../hooks/useElementVisibility';
import GTMValues from 'ui/constants/GTMValues';
import ErrorScreen from '../../molecules/ErrorScreen';
import { IRootState } from '../../../store';
import { useRouter } from 'next/router';
import { IMetadata } from '../../atoms/Metadata/interface';
import { Article } from '../../../interface';

declare const window: WindowTypes;

const TanyaTasia = dynamic(() => import('ui/components/atoms/TanyaTasia'), {
  ssr: false,
});

const DetailNewsTipsInquiryFormSection = dynamic(
  () => import('ui/components/organism/DetailNewsTipsInquiryFormSection'),
  {
    ssr: false,
  }
);

const InquiryFormMobileNews = dynamic(
  () => import('ui/components/organism/InquiryFormMobileNews'),
  {
    ssr: false,
  }
);

interface IArticleDetailPageTemplateProps {
  article: Article;
  anotherPromo: Article | Article[];
  anotherArticles: Article[];
  metadata: IMetadata;
  errorCode?: number;
  isNativeView: boolean;
  promosiUrl: string;
  breadcrumbItems: IBreadcrumbs[];
  sourcePage: string;
  branchCode: string;
  cityOfTheArticle?: {
    province?: string;
  };
  isBranchTypeBP: boolean;
  feature?: string;
}

const ArticleDetailPageTemplate = ({
  article,
  anotherPromo,
  anotherArticles,
  metadata,
  errorCode,
  breadcrumbItems,
  sourcePage,
  branchCode,
  isBranchTypeBP,
  isNativeView = false,
  cityOfTheArticle = null,
  feature,
}: IArticleDetailPageTemplateProps) => {
  const isMobileScreen = useSelector((state: IRootState) => state.page.isMobileScreen);
  const { isGTMReady } = useSelector((state: any) => state.dataGTM || {});

  const [formOpen, setFormOpen] = useState(false);
  const [isButtonInquiryVisible, setIsButtonInquiryVisible] = useState(false);
  const buttonInquiryListRef = useRef(null);
  const initialButtonPosition = useRef(0);
  const isButtonStuck = useElementVisibility(buttonInquiryListRef);
  const router = useRouter();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setMetadata(metadata));
  }, [metadata, dispatch]);

  if (errorCode) {
    return <ErrorScreen errorCode={errorCode} />;
  }

  useEffect(() => {
    if (isNativeView) {
      window?.androidApp?.showToolbar();
      window?.webkit?.messageHandlers?.iosApp?.postMessage('showToolbar');
    }

    const handleScroll = () => {
      if (buttonInquiryListRef.current) {
        const currentButtonPosition = buttonInquiryListRef.current.getBoundingClientRect().top;
        const scrollDifference = initialButtonPosition.current - currentButtonPosition;

        // Jika perubahan posisi lebih dari 100px, atur setIsButtonInquiryVisible menjadi false
        if (scrollDifference > 25 && currentButtonPosition <= initialButtonPosition.current) {
          setIsButtonInquiryVisible(true);
        } else {
          setIsButtonInquiryVisible(false);
        }
      }
    };

    // Simpan posisi awal elemen saat komponen dimount
    initialButtonPosition.current =
      buttonInquiryListRef?.current && buttonInquiryListRef.current.getBoundingClientRect().top;

    // Tambahkan event listener untuk mendengarkan perubahan scroll
    window.addEventListener('scroll', handleScroll);

    // Membersihkan event listener saat komponen unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [collectDataLayerPromo, setcollectDataLayerPromo] = useState([]);
  const [collectDataLayerNews, setcollectDataLayerNews] = useState([]);
  useEffect(() => {
    if (collectDataLayerPromo.length > 0) {
      sendDataInChunks(
        collectDataLayerPromo,
        4,
        (chunk) => {
          pageViewPromoAnother(chunk);
        },
        setcollectDataLayerPromo,
        ''
      );
    }

    if (collectDataLayerNews.length > 0) {
      sendDataInChunks(
        collectDataLayerNews,
        4,
        (chunk) => {
          pageViewNewsAnother(chunk);
        },
        setcollectDataLayerNews,
        ''
      );
    }
  }, [collectDataLayerNews, collectDataLayerPromo]);

  const handleOpenInquiryForm = () => {
    setFormOpen(true);
    window?.androidApp?.hideToolbar();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('hideToolbar');

    window?.androidApp?.clickAskOfferNewsTips();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('clickAskOfferNewsTips');
  };

  const handleCloseInquiryForm = () => {
    setFormOpen(false);
    window?.androidApp?.showToolbar();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('showToolbar');
  };

  return (
    <>
      {/* Data structure for SEO */}
      <StructuredData breadcrumbs={breadcrumbItems} newsArticle={article} />

      {/* Breadcrumb for web screen */}
      {!isNativeView && (
        <>
          <BreadCrumbs isMobileScreen={false} items={breadcrumbItems} />

          <div className="container flex justify-start items-center w-full pt-2 pb-2 sticky top-[60px] md:top-[111px] z-30 bg-white md:hidden">
            <button className="h-full" onClick={() => router.back()} aria-label="back button">
              <MdOutlineArrowBack size={24} color="#3D4043" />
            </button>
          </div>
        </>
      )}

      <div className="relative">
        {/* Main content */}
        <div className="lg:container xl:px-0">
          <div
            className={`grid grid-cols-1 gap-6 ${
              isBranchTypeBP
                ? 'lg:grid-cols-1 lg:max-w-[820px] lg:mx-auto'
                : 'lg:grid-cols-[2fr_1fr]'
            }`}
          >
            {/* Col 1 */}
            <div className="disableTextSelection">
              {/* Hero section */}
              {article.heroImageLink && (
                <DetailArticleHeroSection
                  bannerLink={article?.bannerLink}
                  image={article?.heroImageLink}
                  altImage={article?.altImage}
                />
              )}

              {/* Body section */}
              <DetailArticleBodySection
                article={article}
                feature={feature}
                emarsysData={{ breadcrumbs: breadcrumbItems }}
              />
            </div>

            {/* Col 2 */}
            {!isMobileScreen && !isBranchTypeBP && (
              <div className="hidden my-4 md:mt-5 md:block">
                <div className="container">
                  <DetailNewsTipsInquiryFormSection
                    dataArticle={article}
                    title={'Miliki Mobil Baru dengan Mudah'}
                    sourcePage={sourcePage}
                    articleTitle={article.titlePage}
                    branchCode={branchCode}
                    cityOfTheArticle={cityOfTheArticle}
                    subTitle={
                      'Dapatkan penawaran mobil baru Toyota dari Account Executive kami. Data pribadi Anda aman bersama kami.'
                    }
                    impressionData={{
                      breadCrumb: breadcrumbItems,
                    }}
                    feature={feature}
                  />
                </div>
              </div>
            )}
          </div>
          <div id="doxa-form" />
        </div>

        {/* Tanya Tasia & Back to Top Button */}
        {isGTMReady &&
          (!isButtonInquiryVisible ? (
            <TanyaTasia
              backToTopClassName={`${isMobileScreen ? '!bottom-[158px]' : '!bottom-[30px]'}`}
            />
          ) : (
            <TanyaTasia
              backToTopClassName={`${isMobileScreen ? '!bottom-[86px]' : '!bottom-[30px]'}`}
            />
          ))}

        {/* CTA Button for mobile screen */}
        {!isBranchTypeBP && (
          <div
            className={`sticky w-full z-22 mt-2.5 md:hidden ${
              isNativeView ? 'bottom-0' : 'bottom-[69px]'
            } ${isButtonStuck ? 'top-0' : ''}`}
            ref={buttonInquiryListRef}
          >
            <BtnConfirm
              id="btn-mobile-promo-terbaru"
              block={true}
              className="bg-supportiveRed text-white h-[72px]"
              size="large"
              iconType="icon"
              onClick={() => {
                handleOpenInquiryForm();
                eventClickInterest(article, GTMValues.newsTitleTrade);
              }}
            >
              <div className="flex justify-end items-center gap-3">
                <div className="flex flex-col text-end py-3.5">
                  <p className="text-base font-bold uppercase">{GTMValues.newsTitleTrade}</p>
                </div>
                <MdArrowForward size={20} />
              </div>
            </BtnConfirm>
          </div>
        )}
      </div>

      {/* Other promo */}
      <DetailArticleAnotherPromoSection
        handleCollectDataLayer={(data) => setcollectDataLayerPromo((prev) => [...prev, data])}
        anotherPromo={anotherPromo}
        impressionData={{
          breadCrumb: breadcrumbItems,
          article,
        }}
        feature={feature}
      />

      {/* Other articles */}
      <div className="xl:px-0">
        <DetailArticleAnotherArticlesSection
          handleCollectDataLayer={(data) => setcollectDataLayerNews((prev) => [...prev, data])}
          anotherArticles={anotherArticles}
          impressionData={{
            breadCrumb: breadcrumbItems,
            article,
          }}
          feature={feature}
        />
      </div>

      {/* Social media links on top info */}
      {!isMobileScreen && (
        <SocialMediaLinksSection
          article={article}
          feature={feature}
          impressionData={{ breadCrumb: breadcrumbItems }}
        />
      )}

      {/* info digiroom & social media links */}
      <InfoDigiroom />
      {isMobileScreen && (
        <SocialMediaLinksSection
          article={article}
          feature={feature}
          impressionData={{ breadCrumb: breadcrumbItems }}
        />
      )}

      {/* Breadcrumb for mobile screen */}
      {!isNativeView && (
        <BreadCrumbs
          isMobileScreen={true}
          items={breadcrumbItems}
          breadcrumbsClassName={'mt-[38px]'}
        />
      )}

      {/* Inquiry form mobile section */}
      {isMobileScreen && !isBranchTypeBP && (
        <div className="block lg:hidden">
          <InquiryFormMobileNews
            title={'Miliki Mobil Baru dengan Mudah'}
            formOpen={formOpen}
            onClose={handleCloseInquiryForm}
            promo={article}
            sourcePage={sourcePage}
            articleTitle={article.titlePage}
            branchCode={branchCode}
            cityOfTheArticle={cityOfTheArticle}
            impressionData={{
              breadCrumb: breadcrumbItems,
            }}
            feature={feature}
          />
        </div>
      )}
    </>
  );
};

export default ArticleDetailPageTemplate;
